import React, { useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import Heading from '@atlaskit/heading';
import { Stack } from '@atlaskit/primitives';
import TagGroup from '@atlaskit/tag-group';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';
import {
	Card,
	CloseButton,
	Footer,
	OverrideButton,
	type RequestedResponder,
} from '@atlassian/eoc-schedule-common';

import { messages as commonMessages } from '../../../messages';
import { ResponderTag } from '../responder-tag';

import { type MultiShiftCardProps } from './types';

const MultiShiftCard = ({ cards, onClose, onQuickOverride }: MultiShiftCardProps) => {
	const intlApi = useIntl();

	const responders = useMemo(
		() =>
			Object.values(
				cards.reduce(
					(acc, { responder }): Record<string, RequestedResponder> =>
						acc[responder.id] ? acc : { ...acc, [responder.id]: responder },
					{} as Record<string, RequestedResponder>,
				),
			),
		[cards],
	);

	return (
		<Card
			role="dialog"
			aria-labelledby="shift-card-title"
			controls={<CloseButton onClick={onClose} />}
			content={
				<Stack space="space.200">
					<Stack space="space.0">
						<Heading id="shift-card-title" as="h5" size="xxsmall">
							{intlApi.formatMessage(commonMessages.selectionCount, {
								count: cards.length,
							})}
						</Heading>

						<TagGroup>
							{responders.map((responder) => (
								<ResponderTag responder={responder} key={responder.id} />
							))}
						</TagGroup>
					</Stack>

					<Footer>
						<OverrideButton
							onClick={(_, analyticsEvent) => {
								fireUIAnalytics(analyticsEvent, 'bulkOverride');

								onQuickOverride();
							}}
						/>
					</Footer>
				</Stack>
			}
		/>
	);
};

export { MultiShiftCard };
